import React, { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from 'components/AppIcon';

const DURATION = 5000;

export default function PurchaseSuccessToast({ toast, onDismiss }) {
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!toast) { setProgress(100); return; }

    setProgress(100);
    startRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startRef?.current;
      const pct = Math.max(0, 100 - (elapsed / DURATION) * 100);
      setProgress(pct);
      if (pct > 0) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);

    timerRef.current = setTimeout(() => {
      onDismiss?.();
    }, DURATION);

    return () => {
      cancelAnimationFrame(rafRef?.current);
      clearTimeout(timerRef?.current);
    };
  }, [toast]); // eslint-disable-line react-hooks/exhaustive-deps

  const { productName, price, category, remainingBudget, categoryBudget } = toast || {};

  const remainingPct = categoryBudget > 0 ? (remainingBudget / categoryBudget) * 100 : 100;
  const isExceeded = remainingBudget < 0;
  const isWarning = !isExceeded && remainingPct < 20;

  const borderColor = isExceeded ? '#EF4444' : isWarning ? '#F59E0B' : '#22C55E';
  const barColor   = isExceeded ? '#EF4444' : isWarning ? '#F59E0B' : '#22C55E';
  const titleText  = isExceeded ? 'Budget Limit Reached' : 'Purchase Logged! ✓';

  return (
    <AnimatePresence>
      {toast && (
        <motion.div
          key="purchase-success-toast"
          initial={{ opacity: 0, y: -56 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[9999] w-[92%] max-w-sm"
          style={{
            background: 'rgba(15,23,42,0.97)',
            border: `1.5px solid ${borderColor}`,
            borderRadius: 16,
            boxShadow: `0 8px 32px rgba(0,0,0,0.5)`,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            overflow: 'hidden',
          }}
        >
          {/* Body */}
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-start gap-3">
              {/* Animated checkmark circle */}
              <div
                style={{
                  width: 32, height: 32, flexShrink: 0, marginTop: 2,
                  borderRadius: '50%',
                  background: isExceeded ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <circle
                    cx="9" cy="9" r="8"
                    stroke={isExceeded ? '#EF4444' : '#22C55E'}
                    strokeWidth="1.5"
                    fill="none"
                    strokeDasharray="50.3"
                    strokeDashoffset="50.3"
                    style={{ animation: 'psDrawCircle 0.4s ease-out forwards' }}
                  />
                  <polyline
                    points="5,9 8,12 13,6"
                    stroke={isExceeded ? '#EF4444' : '#22C55E'}
                    strokeWidth="1.8"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="14"
                    strokeDashoffset="14"
                    style={{ animation: 'psDrawCheck 0.35s 0.2s ease-out forwards' }}
                  />
                </svg>
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm text-white leading-tight">
                  {titleText}
                </p>
                <p className="text-slate-400 text-xs mt-0.5 leading-snug">
                  <span className="text-slate-300">{productName}</span>
                  {' — '}
                  <span className="font-data text-slate-300">${price?.toFixed(2)}</span>
                  {' deducted from your '}
                  <span style={{ color: '#00E5FF' }}>{category}</span>
                  {' budget'}
                </p>
                <p className="text-xs mt-1" style={{ color: '#00E5FF' }}>
                  Remaining {category} budget:{' '}
                  <span className="font-data font-semibold">
                    {remainingBudget >= 0
                      ? `$${remainingBudget?.toFixed(2)}`
                      : `-$${Math.abs(remainingBudget)?.toFixed(2)}`}
                  </span>
                </p>
                {isExceeded && (
                  <p className="text-xs mt-1" style={{ color: '#EF4444' }}>
                    You've exceeded your {category} budget by{' '}
                    <span className="font-data font-semibold">${Math.abs(remainingBudget)?.toFixed(2)}</span>
                  </p>
                )}
                {isWarning && !isExceeded && (
                  <p className="text-xs mt-1" style={{ color: '#F59E0B' }}>
                    ⚠ Only{' '}
                    <span className="font-data font-semibold">${remainingBudget?.toFixed(2)}</span>
                    {' '}left in {category} budget this month
                  </p>
                )}
              </div>

              {/* Dismiss × */}
              <button
                onClick={onDismiss}
                className="flex-shrink-0 -mt-0.5 -mr-1 p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
                aria-label="Dismiss toast"
              >
                <Icon name="X" size={14} color="#64748B" />
              </button>
            </div>
          </div>

          {/* Depleting progress bar */}
          <div style={{ height: 2, background: '#1E293B' }}>
            <div
              style={{
                height: '100%',
                width: `${progress}%`,
                background: barColor,
                transition: 'width 0.1s linear',
              }}
            />
          </div>

          <style>{`
            @keyframes psDrawCircle {
              from { stroke-dashoffset: 50.3; }
              to   { stroke-dashoffset: 0; }
            }
            @keyframes psDrawCheck {
              from { stroke-dashoffset: 14; }
              to   { stroke-dashoffset: 0; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
